import React, { useState } from 'react'
import classNames from 'classnames'
import { useAppSelector } from '../../../store/hooks'
import { selectCrossClientResultsExecution, selectCrossClientSelectedTab } from '../../../store/crossClient/crossClientResults'
import { CrossClientQueryEvaluationTypes, type CrossClientQueryResult } from '../../../models/CrossClient'
import { Button, type ButtonVariant } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { getCrossClientNextStep } from './crossClientNextStep'
import { useCrossClientEvaluationActions } from './useCrossClientEvaluationActions'
import { CROSS_CLIENT_AI_EVAL_BLOCKED_MESSAGE, CROSS_CLIENT_LOCAL_RESOLVER_BLOCKED_MESSAGE } from '../utils/crossClientFunds'
import { useCrossClientFundsQuotas } from '../utils/crossClientFundsQuotas'

export interface CrossClientContinueButtonProps {
  result: CrossClientQueryResult
  className?: string
  variant?: ButtonVariant
  withTooltip?: boolean
}

export const CrossClientContinueButton: React.FC<CrossClientContinueButtonProps> = ({ result, className, variant = 'header', withTooltip = true }) => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const { evaluateResults } = useCrossClientEvaluationActions()
  const { canAiEval, canLocalResolverEval, quotaLoading } = useCrossClientFundsQuotas()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const nextStep = getCrossClientNextStep(selectedTab, result, execution)
  const isAiStep = nextStep.kind === CrossClientQueryEvaluationTypes.AI
  const fundsBlocked = isAiStep ? !canAiEval : !canLocalResolverEval
  const busy = !!result.activeEvaluationId || isSubmitting
  const disabled = busy || fundsBlocked || quotaLoading

  const handleClick = async () => {
    if (disabled) {
      return
    }
    setIsSubmitting(true)
    try {
      await evaluateResults([result.id], nextStep.kind)
    } finally {
      setIsSubmitting(false)
    }
  }

  const button = (
    <Button
      variant={variant}
      className={classNames('button-small rounded-3 text-nowrap zocks-border-0.5 zocks-border-color-gray-300', className, {
        'zocks-bg-color-gray-100 zocks-text-gray-500': busy,
      })}
      disabled={disabled}
      aria-disabled={disabled}
      loading={busy}
      onClick={disabled ? undefined : () => void handleClick()}
    >
      Continue
    </Button>
  )

  const blockedMessage = isAiStep ? CROSS_CLIENT_AI_EVAL_BLOCKED_MESSAGE : CROSS_CLIENT_LOCAL_RESOLVER_BLOCKED_MESSAGE
  const tooltipTitle = quotaLoading ? undefined : fundsBlocked ? blockedMessage : withTooltip ? nextStep.tooltip : undefined

  return tooltipTitle ? (
    <Tooltip placement='top' title={tooltipTitle}>
      {button}
    </Tooltip>
  ) : (
    button
  )
}
